<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  board: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const localSteps = ref([]);
const showAddForm = ref(false);
const newName = ref('');
const newColor = ref('#475569');
const editingId = ref(null);
const editName = ref('');
const editColor = ref('');

const orderedSteps = computed(() => {
  const order = props.board.steps_order || [];
  const steps = props.board.steps || [];
  if (!order.length) return steps;
  const byId = new Map(steps.map(s => [s.id, s]));
  const ordered = order.map(id => byId.get(id)).filter(Boolean);
  steps.forEach(s => {
    if (!ordered.includes(s)) ordered.push(s);
  });
  return ordered;
});

watch(
  orderedSteps,
  steps => {
    localSteps.value = [...steps];
  },
  { immediate: true }
);

const persistOrder = async () => {
  try {
    await store.dispatch('kanban/boards/update', {
      id: props.board.id,
      board: { steps_order: localSteps.value.map(s => s.id) },
    });
  } catch (error) {
    useAlert(t('KANBAN.SETTINGS.STEPS.REORDER_ERROR'));
  }
};

const addStep = async () => {
  if (!newName.value.trim()) return;
  try {
    await store.dispatch('kanban/boardSteps/create', {
      boardId: props.board.id,
      name: newName.value.trim(),
      color: newColor.value,
    });
    newName.value = '';
    newColor.value = '#475569';
    showAddForm.value = false;
    useAlert(t('KANBAN.SETTINGS.STEPS.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(t('KANBAN.SETTINGS.STEPS.CREATE_ERROR'));
  }
};

const startEdit = step => {
  editingId.value = step.id;
  editName.value = step.name;
  editColor.value = step.color || '#475569';
};

const cancelEdit = () => {
  editingId.value = null;
};

const saveEdit = async step => {
  try {
    await store.dispatch('kanban/boardSteps/update', {
      boardId: props.board.id,
      id: step.id,
      name: editName.value.trim() || step.name,
      color: editColor.value,
    });
    editingId.value = null;
    useAlert(t('KANBAN.SETTINGS.STEPS.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('KANBAN.SETTINGS.STEPS.UPDATE_ERROR'));
  }
};

const toggleCancelled = async step => {
  try {
    await store.dispatch('kanban/boardSteps/update', {
      boardId: props.board.id,
      id: step.id,
      cancelled: !step.cancelled,
    });
  } catch (error) {
    useAlert(t('KANBAN.SETTINGS.STEPS.UPDATE_ERROR'));
  }
};

const removeStep = async step => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('KANBAN.SETTINGS.STEPS.CONFIRM_DELETE'))) return;
  try {
    await store.dispatch('kanban/boardSteps/delete', {
      boardId: props.board.id,
      id: step.id,
    });
    useAlert(t('KANBAN.SETTINGS.STEPS.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('KANBAN.SETTINGS.STEPS.DELETE_ERROR'));
  }
};
</script>

<template>
  <section class="w-full">
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-heading-2 text-n-slate-12">
        {{ $t('KANBAN.SETTINGS.STEPS.TITLE') }}
      </h2>
      <Button
        :label="$t('KANBAN.SETTINGS.STEPS.ADD')"
        icon="i-lucide-plus"
        size="sm"
        @click="showAddForm = !showAddForm"
      />
    </div>

    <div
      v-if="showAddForm"
      class="flex items-end gap-3 p-3 mb-4 border border-n-weak rounded-md"
    >
      <woot-input
        v-model="newName"
        class="flex-1"
        :label="$t('KANBAN.SETTINGS.STEPS.NAME')"
        :placeholder="$t('KANBAN.SETTINGS.STEPS.NAME_PLACEHOLDER')"
      />
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ $t('KANBAN.SETTINGS.STEPS.COLOR') }}
        </label>
        <input v-model="newColor" type="color" class="h-9 w-14 cursor-pointer" />
      </div>
      <Button
        :label="$t('KANBAN.SETTINGS.STEPS.CREATE')"
        :disabled="!newName.trim()"
        @click="addStep"
      />
    </div>

    <Draggable
      v-model="localSteps"
      item-key="id"
      handle=".drag-handle"
      class="flex flex-col gap-2"
      ghost-class="opacity-40"
      @end="persistOrder"
    >
      <template #item="{ element: step }">
        <div
          class="flex items-center gap-3 p-3 border border-n-weak rounded-md bg-n-alpha-1"
        >
          <span class="drag-handle cursor-grab text-n-slate-11">
            <span class="i-lucide-grip-vertical inline-block size-4" />
          </span>

          <template v-if="editingId === step.id">
            <input v-model="editColor" type="color" class="h-8 w-12 cursor-pointer" />
            <input
              v-model="editName"
              type="text"
              class="flex-1 px-2 py-1 border border-n-weak rounded"
            />
            <Button size="sm" :label="$t('KANBAN.SETTINGS.STEPS.SAVE')" @click="saveEdit(step)" />
            <Button size="sm" faded slate :label="$t('KANBAN.SETTINGS.STEPS.CANCEL')" @click="cancelEdit" />
          </template>

          <template v-else>
            <span
              class="size-4 rounded-full"
              :style="{ backgroundColor: step.color || '#475569' }"
            />
            <span class="flex-1 text-body-main text-n-slate-12">{{ step.name }}</span>
            <span
              v-if="step.cancelled"
              class="text-xs px-2 py-0.5 rounded bg-n-ruby-2 text-n-ruby-11"
            >
              {{ $t('KANBAN.SETTINGS.STEPS.CANCELLED_BADGE') }}
            </span>
            <Button
              v-tooltip.top="$t('KANBAN.SETTINGS.STEPS.TOGGLE_CANCELLED')"
              icon="i-lucide-x-circle"
              slate
              sm
              @click="toggleCancelled(step)"
            />
            <Button
              v-tooltip.top="$t('KANBAN.SETTINGS.ACTIONS.EDIT')"
              icon="i-lucide-pencil"
              slate
              sm
              @click="startEdit(step)"
            />
            <Button
              v-tooltip.top="$t('KANBAN.SETTINGS.ACTIONS.DELETE')"
              icon="i-lucide-trash"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="removeStep(step)"
            />
          </template>
        </div>
      </template>
    </Draggable>

    <p
      v-if="!localSteps.length"
      class="text-body-small text-n-slate-11 text-center py-6"
    >
      {{ $t('KANBAN.SETTINGS.STEPS.EMPTY') }}
    </p>
  </section>
</template>
